<template>
  <v-card class="card-container">
    <div class="main-toolbar mb-4">
      <div class="my-1 mx-3">
        <div class="d-flex align-end">
          <div class="icon-back">
            <v-icon id="icon" size="37" color="white">
              mdi-chart-areaspline
            </v-icon>
          </div>

          <v-toolbar-title class="main-toolbar-title pb-1">
            {{ $t('Agendamentos - Cronograma') }}
          </v-toolbar-title>
        </div>
      </div>
    </div>

    <v-main id="table" class="back pa-3">
      <v-container>
        <v-row class="ma-0" dense>
          <v-col cols="12" sm="3">
            <label class="label">{{ $t('Instituição') }}</label>
            <v-autocomplete
              hide-details="auto"
              v-model="institutionId"
              :items="institutions"
              item-text="name"
              item-value="id"
              :loading="institutionLoading"
              disabled
              outlined
              class="primary-input"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" sm="3">
            <label class="label">{{ $t('Unidades') }}</label>
            <v-autocomplete
              :search-input.sync="unitySearch"
              @blur="unitySearch = ''"
              hide-details="auto"
              v-model="unidadeSelected"
              :items="unidades"
              item-text="name"
              :loading="unidadeLoading"
              :disabled="!institutionId"
              :placeholder="
                !institutionId ? $t('Selecione uma instituição') : $t('Todas')
              "
              multiple
              deletable-chips
              return-object
              outlined
              class="primary-input"
              @change="getDepartments"
            >
              <template v-slot:selection="{ attrs, item, select, selected }">
                <v-chip
                  v-bind="attrs"
                  :input-value="selected"
                  close
                  color="#BBCF20"
                  text-color="#fff"
                  small
                  @click="select"
                  @click:close="removeUnidade(item)"
                >
                  {{ item.name }}
                </v-chip>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12" sm="3">
            <label class="label">{{ $t('Setor') }}</label>
            <v-autocomplete
              :search-input.sync="departmentSearch"
              :disabled="unidadeSelected.length == 0"
              @blur="departmentSearch = ''"
              hide-details="auto"
              v-model="departmentSelected"
              :items="departments"
              item-text="name"
              multiple
              deletable-chips
              return-object
              placeholder="Todos"
              outlined
              class="primary-input"
              @change="getSubDepartments"
            >
              <template v-slot:selection="{ attrs, item, select, selected }">
                <v-chip
                  v-bind="attrs"
                  :input-value="selected"
                  close
                  color="#BBCF20"
                  text-color="#fff"
                  small
                  @click="select"
                  @click:close="removeDepartment(item)"
                >
                  {{ item.name }}
                </v-chip>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12" sm="3">
            <label class="label">{{ $t('Subsetor') }}</label>
            <v-autocomplete
              :search-input.sync="subDepartmentSearch"
              :disabled="!departmentSelected.length"
              @blur="subDepartmentSearch = ''"
              hide-details="auto"
              v-model="subDepartmentSelected"
              :items="subDepartments"
              item-text="name"
              multiple
              deletable-chips
              return-object
              placeholder="Todos"
              outlined
              class="primary-input"
            >
              <template v-slot:selection="{ attrs, item, select, selected }">
                <v-chip
                  v-bind="attrs"
                  :input-value="selected"
                  close
                  color="#BBCF20"
                  text-color="#fff"
                  small
                  @click="select"
                  @click:close="removeSubdepartment(item)"
                >
                  {{ item.name }}
                </v-chip>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12" sm="3">
            <label class="label">{{ $t('Situação') }}</label>
            <v-autocomplete
              hide-details="auto"
              v-model="equipmentSituation"
              :items="situations"
              outlined
              class="primary-input"
              @keyup.enter="filterTable"
            >
              <template v-slot:selection="{ item }">
                <span class="text-caption">
                  {{ $t(item) }}
                </span>
              </template>

              <template v-slot:item="{ item }">
                <span class="text-caption">
                  {{ $t(item) }}
                </span>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12" sm="3">
            <label class="label">{{ $t('Equipamento padrão') }}</label>
            <v-autocomplete
              hide-details="auto"
              chips
              clearable
              v-model="standardEquipmentSelected"
              :items="standardEquipment"
              return-object
              item-text="name"
              multiple
              deletable-chips
              small-chips
              outlined
              class="primary-input"
              placeholder="Todos"
              :loading="standardEquipmentLoading"
              :search-input.sync="search"
              @blur="search = ''"
            >
              <template v-slot:item="{ item }">
                {{ item.standardEquipment.name }}
              </template>
              <template
                v-slot:selection="{ attrs, item, index, select, selected }"
              >
                <v-chip
                  v-if="index === 0"
                  v-bind="attrs"
                  :input-value="selected"
                  close
                  color="#BBCF20"
                  text-color="#fff"
                  small
                  @click="select"
                  @click:close="removeStandardEquipment(item)"
                >
                  {{ item.standardEquipment.name }}
                </v-chip>
                <span
                  v-if="index === 1"
                  class="grey--text text-caption"
                  style="font-size: 0.8rem"
                >
                  ( +{{ standardEquipmentSelected.length - 1 }})
                </span>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12" sm="3">
            <label class="label">{{ $t('Identificação') }}</label>
            <v-text-field
              v-model="identification"
              hide-details="auto"
              clearable
              small-chips
              outlined
              class="primary-input"
            >
            </v-text-field>
          </v-col>
          <v-col cols="12" sm="3">
            <label class="label">{{ $t('Código Patrimônio') }}</label>
            <v-text-field
              v-model="propertyCode"
              hide-details="auto"
              clearable
              small-chips
              outlined
              class="primary-input"
            >
            </v-text-field>
          </v-col>
          <v-col cols="12" sm="6" lg="3">
            <label class="label">{{ $t('Responsável') }}</label>
            <v-autocomplete
              hide-details="auto"
              v-model="responsible"
              :items="responsibles"
              item-text="name"
              item-value="id"
              outlined
              class="primary-input"
            >
            </v-autocomplete>
          </v-col>
          <v-col cols="12" sm="6" lg="3">
            <label class="label">{{ $t('Responsabilidade') }}</label>
            <v-autocomplete
              hide-details="auto"
              v-model="responsibility"
              :items="responsibilities"
              outlined
              class="primary-input"
            ></v-autocomplete>

            <div v-if="responsibility == 'Interna' || responsibility == 0">
              <v-checkbox
                hide-details="auto"
                :label="$t('Equipe Volante')"
                v-model="internalTeam"
                outlined
                class="mt-0 ml-2"
              ></v-checkbox>
            </div>
          </v-col>

          <v-col
            cols="12"
            sm="6"
            md="3"
            lg="3"
            v-if="institution.departmentTypes.includes(1)"
          >
            <label class="label ml-1">{{ $t('Oficina') }}</label>
            <v-autocomplete
              hide-details="auto"
              v-model="workshopId"
              :items="workshopTypes"
              :rules="[(v) => v != null || 'Oficina obrigatório']"
              item-text="name"
              item-value="id"
              autocomplete="off"
              outlined
              class="primary-input"
            >
              <template #message="{ message }">
                {{ $t(message) }}
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12" sm="3">
            <label class="label">{{ $t('Tipo') }}</label>
            <v-autocomplete
              @blur="typeSearch = ''"
              :search-input.sync="typeSearch"
              hide-details="auto"
              v-model="serviceOrderTypeSelected"
              :items="serviceOrderTypes"
              item-text="name"
              item-value="value"
              multiple
              deletable-chips
              placeholder="Todos"
              outlined
              class="primary-input"
            >
              <template v-slot:selection="{ attrs, item, select, selected }">
                <v-chip
                  v-bind="attrs"
                  :input-value="selected"
                  close
                  color="#BBCF20"
                  text-color="#fff"
                  small
                  @click="select"
                  @click:close="removeServiceOrderType(item)"
                >
                  {{ item.name }}
                </v-chip>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12" sm="6" lg="3">
            <label class="label">{{ $t('Estado') }}</label>
            <v-autocomplete
              hide-details="auto"
              v-model="state"
              item-text="text"
              item-value="value"
              :items="states"
              outlined
              multiple
              class="primary-input"
            >
              <template v-slot:selection="{ attrs, item, select, selected }">
                <v-chip
                  v-bind="attrs"
                  :input-value="selected"
                  close
                  color="#BBCF20"
                  text-color="#fff"
                  small
                  @click="select"
                  @click:close="removeStatus(item)"
                >
                  {{ item.text }}
                </v-chip>
              </template>
            </v-autocomplete>

            <v-checkbox
              v-model="correctiveMaintenanceEquipment"
              hide-details
              label="Equipamentos em Manutenção Corretiva"
            ></v-checkbox>
          </v-col>

          <v-col cols="12" sm="3">
            <v-row no-gutters class="ma-0">
              <v-col cols="12" class="d-flex justify-center">
                <label class="label"> {{ $t('Período') }}: </label>
              </v-col>
              <v-col cols="6">
                <v-autocomplete
                  hide-details="auto"
                  v-model="month"
                  :items="months"
                  outlined
                  :placeholder="$t('Mês')"
                  class="primary-input mr-1"
                ></v-autocomplete>
              </v-col>
              <v-col cols="6">
                <v-autocomplete
                  hide-details="auto"
                  v-model="year"
                  :items="years"
                  outlined
                  :placeholder="$t('Ano')"
                  class="primary-input"
                ></v-autocomplete>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12" sm="3">
            <label class="label"> {{ $t('Formato') }}: </label>
            <v-autocomplete
              hide-details="auto"
              v-model="format"
              :items="formats"
              outlined
              class="primary-input"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" class="d-flex justify-end">
            <div class="d-flex align-center justify-center">
              <v-btn
                :loading="loading"
                class="mx-4 primary-button"
                color="#0F3565"
                @click="scheduleExport"
              >
                <span>Exportar</span>
                <v-icon class="pl-2">mdi-download</v-icon>
              </v-btn>
              <v-btn color="#20202066" class="clear-button" @click="reset">
                <span>{{ $t('Limpar') }}</span>
                <v-icon class="pl-2">mdi-cached</v-icon>
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-main>
  </v-card>
</template>

<script>
import ApiService from '../../../services/ApiService'
import GeneralService from '../../../services/GeneralService'

export default {
  name: 'ChronogramPage',
  async created() {
    this.userLogged = JSON.parse(localStorage.getItem('user'))
    this.institution = JSON.parse(localStorage.getItem('institution'))
    this.institutionId = this.institution.id
    this.getInstitutions()
    this.getStandardEquipment()
    this.getWorkshops()
    this.getresponsibles()

    if (this.institution.userType > 1) {
      await this.getUserUnidades()
    } else {
      await this.getUnidades()
    }

    if (this.institution.userType == 5) {
      this.unidadeSelected = this.unidades.filter(
        (x) => x.id === this.userLogged.unidadeId
      )
      this.getDepartments()
    }

    const currentYear = new Date().getFullYear()
    this.years.unshift(currentYear)

    for (let i = 0; i < 3; i++) {
      const year = currentYear - i
      this.years.unshift(year)
    }

    for (let i = 0; i < 3; i++) {
      const year = currentYear + i
      this.years.push(year)
    }

    this.year = currentYear

    this.format = this.formats[0]
  },

  data: () => ({
    correctiveMaintenanceEquipment: false,
    workshopId: null,
    workshopTypes: [],
    institution: {},
    typeSearch: '',
    departmentSearch: '',
    subDepartmentSearch: '',
    departmentIds: [],
    unitySearch: '',
    identification: '',
    propertyCode: '',
    loading: false,
    institutionLoading: false,
    unidadeLoading: false,
    departmentLoading: false,
    standardEquipmentLoading: false,
    menu: false,
    date: null,
    month: null,
    year: null,
    format: null,
    internalTeam: null,
    responsibility: null,
    responsible: null,
    institutionId: null,
    search: null,
    institutions: [],
    unidadeSelected: [],
    state: null,
    states: [
      { text: 'Agendada', value: 0 },
      { text: 'Executada dentro do prazo', value: 1 },
      { text: 'Executada fora do prazo', value: 2 },
      { text: 'Atrasada', value: 3 },
    ],
    unidades: [],
    standardEquipmentSelected: [],
    standardEquipment: [],
    departmentSelected: [],
    subDepartmentSelected: [],
    departments: [],
    subDepartments: [],
    serviceOrderTypeSelected: [],
    userLogged: {},
    apiService: new ApiService(),
    generalService: new GeneralService(),
    equipmentSituation: null,
    situations: [
      'Aluguel',
      'Comodato',
      'De médico/Equipe',
      'Empréstimo',
      'Próprio',
      'Terceiros',
      'Convênio',
    ],
    serviceOrderTypes: [
      {
        name: 'Treinamento',
        value: 0,
      },
      {
        name: 'Segurança elétrica',
        value: 1,
      },
      {
        name: 'Preventiva',
        value: 2,
      },
      {
        name: 'Calibração',
        value: 6,
      },
      {
        name: 'Validação/Qualificação',
        value: 7,
      },
    ],
    responsibilities: ['Interna', 'Externa'],
    responsibles: [],
    months: [
      'Jan',
      'Fev',
      'Mar',
      'Abr',
      'Mai',
      'Jun',
      'Jul',
      'Ago',
      'Set',
      'Out',
      'Nov',
      'Dez',
    ],
    years: [],
    formats: ['HTML', 'Excel', 'PDF'],
  }),

  components: {},

  methods: {
    getWorkshops() {
      this.apiService
        .get('workshop/' + this.institution.id)
        .then((response) => {
          this.workshopTypes = response.content
        })
    },

    async getUserUnidades() {
      this.unidadeLoading = true

      await this.apiService
        .get(`unidade/listByUserAndInstitution/${this.institutionId}`)
        .then((response) => {
          this.unidades = response.content
          this.unidadeLoading = false
        })
    },

    getresponsibles() {
      this.apiService
        .get(`user/byType/${this.institutionId}`)
        .then((response) => {
          this.responsibles = response.content
        })
    },

    removeUnidade(item) {
      this.unidadeSelected.splice(this.unidadeSelected.indexOf(item), 1)
      this.unidadeSelected = [...this.unidadeSelected]
    },

    removeStandardEquipment(item) {
      this.standardEquipmentSelected.splice(
        this.standardEquipmentSelected.indexOf(item),
        1
      )
      this.standardEquipmentSelected = [...this.standardEquipmentSelected]
    },

    remove(item) {
      this.serviceOrderTypeSelected.splice(
        this.serviceOrderTypeSelected.indexOf(item),
        1
      )
      this.serviceOrderTypeSelected = [...this.serviceOrderTypeSelected]
    },

    removeStatus(item) {
      this.state.splice(this.state.indexOf(item), 1)
      this.state = [...this.state]
    },

    removeDepartment(item) {
      this.departmentSelected.splice(this.departmentSelected.indexOf(item), 1)
      this.departmentSelected = [...this.departmentSelected]
    },

    removeSubdepartment(item) {
      this.subDepartmentSelected.splice(
        this.subDepartmentSelected.indexOf(item),
        1
      )
      this.subDepartmentSelected = [...this.subDepartmentSelected]
    },

    async getInstitutions() {
      this.institutionLoading = true

      await this.apiService.get('institution/list').then((response) => {
        this.institutions = response.content
        this.institutionLoading = false
      })
    },

    async getUnidades() {
      this.unidadeLoading = true

      await this.apiService
        .get(`unidade/list/${this.institutionId}`)
        .then((response) => {
          this.unidades = response.content
          this.unidadeLoading = false
        })
    },

    async getStandardEquipment() {
      this.standardEquipmentLoading = true

      this.standardEquipment =
        await this.generalService.getStandardEquipmentsByInstitution(
          this.institutionId
        )

      this.standardEquipmentLoading = false
    },

    async getDepartments() {
      this.departmentLoading = true

      await this.apiService
        .post(
          `department/list/unidades`,
          this.unidadeSelected.map((x) => x.id)
        )
        .then((response) => {
          this.departments = response.content
          this.departmentLoading = false
        })
    },

    async getSubDepartments() {
      if (!this.departmentSelected.length) {
        this.subDepartments = []
        return
      }
      const departmentIds = this.departmentSelected.map((dep) => dep.id)
      this.subDepartments = []

      for (let id of departmentIds) {
        await this.apiService
          .get(`department/list-subdepartment/${id}`)
          .then((response) => {
            this.subDepartments = [...this.subDepartments, ...response.content]
          })
      }
    },

    async scheduleExport() {
      this.loading = true

      const types = this.serviceOrderTypeSelected

      const filter = {
        internalTeam: this.internalTeam,
        idEquipment: this.identification,
        propertyCode: this.propertyCode,
        InstitutionId: this.institutionId,
        Unidades: this.unidadeSelected,
        StandardEquipment: this.standardEquipmentSelected,
        ServiceOrderTypes: types,
        Departments: this.departmentSelected,
        Month: this.months.indexOf(this.month) + 1,
        Year: this.year,
        ExportType: this.format == 'HTML' ? 'Front' : this.format,
        Responsibility:
          this.responsibility != null
            ? this.responsibilities.indexOf(this.responsibility)
            : null,
        Responsible: this.responsible,
        BaseUrl: window.location.origin,
        ChronogramStatus: this.correctiveMaintenanceEquipment
          ? this.state
            ? [...this.state, 4]
            : [4]
          : this.state,
        situation: this.situations.indexOf(this.equipmentSituation),
        workshopId: this.workshopId,
      }

      if (filter.Responsibility == 1) filter.internalTeam = null

      await this.apiService
        .post('serviceOrder/scheduleExport', filter)
        .then((response) => {
          localStorage.setItem('scheduleFilters', JSON.stringify(filter))
          window.open('/schedule', '_blank')

          if (this.format == 'PDF') {
            var pdfResult =
              'data:application/pdf;base64,' + response.content.pdf
            const link = document.createElement('a')
            link.href = pdfResult
            link.setAttribute('download', `Cronograma ${this.year}`)
            document.body.appendChild(link)
            link.click()
          }

          if (this.format == 'Excel') {
            var excelResult = `data:application/octet-stream;base64,${response.content.excel}`
            const link = document.createElement('a')
            link.href = excelResult
            link.setAttribute('download', `Cronograma ${this.year}.xlsx`)
            document.body.appendChild(link)
            link.click()
          }
        })
        .catch((err) => {})

      this.loading = false
    },

    reset() {
      if (this.institution.userType == 5) {
        this.unidadeSelected = this.unidades.filter(
          (x) => x.id === this.userLogged.unidadeId
        )
      } else {
        this.unidadeSelected = []
      }

      this.internalTeam = null
      this.standardEquipmentSelected = []
      this.state = null
      this.serviceOrderTypeSelected = []
      this.departmentSelected = []
      this.month = null
      this.year = new Date().getFullYear()
      this.format = this.formats[0]
      this.responsibility = null
      this.responsible = null
    },
  },

  watch: {
    serviceOrderTypeSelected: {
      deep: true,
      handler() {
        this.typeSearch = ''
      },
    },

    standardEquipmentSelected() {
      this.search = ''
    },
  },
}
</script>

<style scoped></style>
